/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import {
  BackgroundLocalComponent,
  BackgroundServerComponent,
  backgroundLocalFunctionList,
  backgroundServerFunctionList,
  batchBackgroundServerFunctionList,
  GTParamsDefinition,
  LoopComponent,
  loopLtypeList,
  PlatformComponent,
  platformDTypeList,
  StartComponent,
  UserComponent,
  userDTypeList,
} from '@karya/core';
import { MessageDisplay } from '@karya/core/src/Index';
import { ParameterArray, ParameterDefinition, ParameterSection } from '@karya/parameter-specs';

export type GenericTaskComponent =
  | PlatformComponent
  | UserComponent
  | BackgroundLocalComponent
  | BackgroundServerComponent
  | LoopComponent;
export type ComponentParameterDefinition<T extends GenericTaskComponent> = ParameterDefinition<keyof T, T>;

export const platformMessageDisplayList: Record<MessageDisplay, string> = {
  EXAMPLE: 'Example',
  INSTRUCTION: 'Instruction',
  REFERENCE: 'Reference',
  TUTORIAL: 'Tutorial',
  DEFAULT: 'Default',
};

export const userDType: ComponentParameterDefinition<UserComponent> = {
  id: 'dtype',
  label: 'Component Data Type',
  description: 'Data type of the component',
  type: 'enum',
  list: userDTypeList,
  required: true,
};

// Optional, mirroring the component type: the renderers fall back to `defaultMessageDisplay`
// when a component does not carry one.
export const platformMessageDisplay: ComponentParameterDefinition<PlatformComponent> = {
  id: 'messageDisplay',
  label: 'Message Display',
  description: 'How the message should be displayed to the user with labels',
  type: 'enum',
  list: platformMessageDisplayList,
  required: false,
};

export const platformDType: ComponentParameterDefinition<PlatformComponent> = {
  id: 'dtype',
  label: 'Component Data Type',
  description: 'Data type of the component',
  type: 'enum',
  list: platformDTypeList,
  required: true,
};

export const localFunctionType: ComponentParameterDefinition<BackgroundLocalComponent> = {
  id: 'function_id',
  label: 'Function Type',
  description: 'Type of function to execute within the background component',
  type: 'enum',
  list: backgroundLocalFunctionList,
  required: true,
};

export const serverFunctionType: ComponentParameterDefinition<BackgroundServerComponent> = {
  id: 'function_id',
  label: 'Function Type',
  description: 'Type of function to execute within the background server component',
  type: 'enum',
  list: backgroundServerFunctionList,
  required: true,
};

// Batch background server function type
export const batchServerFunctionType: ComponentParameterDefinition<BackgroundServerComponent> = {
  id: 'function_id',
  label: 'Function Type',
  description: 'Type of function to execute within the batch background server component',
  type: 'enum',
  list: batchBackgroundServerFunctionList,
  required: true,
};

export const loopComponentLtype: ComponentParameterDefinition<LoopComponent> = {
  id: 'ltype',
  label: 'Function Type',
  description: 'Type of function to execute within the background component',
  type: 'enum',
  list: loopLtypeList,
  required: true,
};

// TODO: Change to NgGenericTask[string]
export const metadataSection: ParameterSection = {
  label: 'Component Metadata',
  description: 'Basic information about the component that you are creating',
  required: true,
  parameters: [
    {
      id: 'key',
      label: 'Key',
      description: 'Component Key',
      type: 'string',
      required: true,
    },
    {
      id: 'name',
      label: 'Name',
      description: 'Name of the component',
      type: 'string',
      required: true,
    },
    {
      id: 'description',
      label: 'Component Description',
      description: 'Description of the component',
      type: 'string',
      required: false,
    },
    {
      id: 'ctype',
      label: 'Component Type',
      description: 'Type of the component',
      type: 'enum',
      list: {
        PLATFORM: 'Platform',
        USER: 'User',
        BACKGROUND_LOCAL: 'Background (Local)',
        BACKGROUND_SERVER: 'Background (Server)',
        BATCH_BACKGROUND_SERVER: 'Batch Background (Server)',
        LOOP: 'Loop',
      },
      required: true,
    },
  ],
};

export const startComponentMetadataParameters: ParameterArray<StartComponent> = [
  {
    id: 'key',
    label: 'Key',
    description: 'Component Key',
    type: 'fixed',
    value: 'start',
    required: false,
  },
  {
    id: 'name',
    label: 'Name',
    description: 'Name of the component',
    type: 'fixed',
    value: 'Start Component',
    required: false,
  },
  {
    id: 'description',
    label: 'Component Description',
    description: 'Description of the component',
    type: 'fixed',
    value: 'First Component of the task specification',
    required: false,
  },
  {
    id: 'ctype',
    label: 'Component Type',
    description: 'Type of the component',
    type: 'enum',
    list: {
      START: 'Specification Start',
      PLATFORM: 'Platform',
      USER: 'User',
      BACKGROUND_LOCAL: 'Background (Local)',
      BACKGROUND_SERVER: 'Background (Server)',
      BATCH_BACKGROUND_SERVER: 'Batch Background (Server)',
      LOOP: 'Loop',
    },
    required: true,
  },
];
export const endComponentMetadataParameters: ParameterArray<StartComponent> = [
  {
    id: 'key',
    label: 'Key',
    description: 'Component Key',
    type: 'fixed',
    value: 'end',
    required: false,
  },
  {
    id: 'name',
    label: 'Name',
    description: 'Name of the component',
    type: 'fixed',
    value: 'End Component',
    required: false,
  },
  {
    id: 'description',
    label: 'Component Description',
    description: 'Description of the component',
    type: 'fixed',
    value: 'Last Component of the task specification',
    required: false,
  },
  {
    id: 'ctype',
    label: 'Component Type',
    description: 'Type of the component',
    type: 'enum',
    list: {
      END: 'Specification End',
      PLATFORM: 'Platform',
      USER: 'User',
      BACKGROUND_LOCAL: 'Background (Local)',
      BACKGROUND_SERVER: 'Background (Server)',
      BATCH_BACKGROUND_SERVER: 'Batch Background (Server)',
      LOOP: 'Loop',
    },
    required: true,
  },
];

// @ts-expect-error next is currently not supported by GTParamsDefinition
export const nextParam: GTParamsDefinition<string, unknown> = {
  id: 'next',
  label: 'Next Component',
  description: 'Reference to the next component',
  // @ts-expect-error Param type for next is always 'REF'
  paramType: 'REF',
  type: 'string',
  required: true,
};
